import {
  Button,
  Col,
  Form,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DELETE_COST_SHEET,
  EXPORT_COST_SHEET,
  GET_COST_SHEET,
  SALES_CONTRACT_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ListTable from "../../../common/ListTable";
import {
  COST_SHEET_REPORT_DELETE_PERMISSION,
  COST_SHEET_REPORT_GENERATE_PERMISSION,
  COST_SHEET_REPORT_VIEW_PERMISSION,
} from "../../../routes/permissions";
import { hasPermission, isArrayAndHasValue } from "../../../utils/functions";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";

const CostSheet = () => {
  // States
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salesContractList, setSalesContractList] = useState(null);
  const [stylesList, setStylesList] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Antd
  const [costSheetform] = Form.useForm();
  const { Option } = Select;
  const { Title } = Typography;

  // Round 2 (B, R3): mounted-ref + a "seed only once" guard for the default
  // sales-contract/style pre-selection.
  // Fix round 1 (B, I3): request-sequence guards (reqIdRef) for both fetches, matching
  // the other six B pages — a fast contract search (onSearch -> getSalesContract) could
  // otherwise let an older response land after a newer one and clobber it.
  const mountedRef = useRef(true);
  const didSeedDefaultRef = useRef(false);
  const salesContractReqIdRef = useRef(0);
  const stylesReqIdRef = useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const cutPath = (path) => {
    const updatedPath = path ? path.split("public/cost_sheet/")[1] : "";
    return updatedPath;
  };

  // Fix round 1 (B, I3/I5): hoisted above getSalesContract and wrapped in useCallback
  // (stable identity — costSheetform is stable per Form.useForm()) so it can be listed
  // in getSalesContract's own dependency array without creating a new getSalesContract
  // on every render (which would re-fire the mount-only useEffect below on every render).
  const getStyles = useCallback(
    async (salesContractId, { autoSelectFirst = false } = {}) => {
      const myReq = ++stylesReqIdRef.current;
      setLoading(true);

      const query = `${SALES_CONTRACT_LIST}/${salesContractId}/edit`;
      const response = await getData(query);

      if (!mountedRef.current || myReq !== stylesReqIdRef.current) return;

      if (response && response?.status === 200) {
        setLoading(false);
        const styles = response?.data?.data?.styleDetail;
        setStylesList(styles);
        // Round 2 (B, R3): only the mount-time default-seed path also picks the
        // contract's own first style; a manual contract change still leaves Style
        // for the user to pick, same as before.
        if (autoSelectFirst && isArrayAndHasValue(styles)) {
          costSheetform.setFieldsValue({
            style_id: styles[0]?.style_info?.id,
          });
        }
      } else {
        setLoading(false);
        message.error("Error getting styles data");
      }
    },
    [costSheetform],
  );

  const getSalesContract = useCallback(
    async (value) => {
      const myReq = ++salesContractReqIdRef.current;
      setLoading(true);

      let query = `${SALES_CONTRACT_LIST}`;
      if (value) {
        query = `${SALES_CONTRACT_LIST}?sales_contract_id=${value}`;
      } else {
        // Round 2 (B, R3): SalesContractController@index now honours this (backward
        // compatible; default unchanged) so the first row of the default (unfiltered)
        // load is the most recently updated contract instead of the highest id.
        // ux2(final-2): renamed sort/order -> order_by/order_dir (whole-branch review I-2 —
        // sort/order collided with the Kyslik sortable() trait's own request('sort')/request('direction')).
        query = `${SALES_CONTRACT_LIST}?order_by=updated_at&order_dir=desc`;
      }
      const response = await getData(query);

      if (!mountedRef.current || myReq !== salesContractReqIdRef.current) return;

      if (response && response?.status === 200) {
        setLoading(false);
        const list = response?.data?.data?.data;
        setSalesContractList(list);

        // Pre-select the most recently updated contract and its own first style,
        // the same way selecting one manually (onChange -> getStyles) would. Only
        // the default-seed path also picks a style automatically; a manual contract
        // change still leaves Style for the user to pick, unchanged.
        if (!value && !didSeedDefaultRef.current && isArrayAndHasValue(list)) {
          didSeedDefaultRef.current = true;
          const defaultContractId = list[0]?.id;
          if (defaultContractId) {
            costSheetform.setFieldsValue({ sales_contract_id: defaultContractId });
            getStyles(defaultContractId, { autoSelectFirst: true });
          }
        }
      } else {
        setLoading(false);
        message.error("Error getting sales contract data");
      }
    },
    [costSheetform, getStyles],
  );

  const getAllCostSheet = useCallback(async () => {
    setFetchError(null);
    setLoading(true);

    const query = `${GET_COST_SHEET}`;
    const response = await getData(query);

    if (response?.status === 200) {
      setLoading(false);
      setTableData(response?.data?.data);
    } else {
      setFetchError("Could not load this list.");
      setLoading(false);
      message.error("Error getting cost sheet data");
    }
  }, []);

  const exportCostSheet = async () => {
    const formValues = costSheetform.getFieldsValue();
    const {
      sales_contract_id,
      style_id,
      cm_cost,
      commercial_cost,
      buying_comm,
    } = formValues;
    const bodyData = {
      sales_contract_id: sales_contract_id,
      style_id: style_id,
      cm_cost: Number(cm_cost),
      commercial_cost: Number(commercial_cost),
      buying_comm: Number(buying_comm),
    };

    setLoading(true);
    const query = `${EXPORT_COST_SHEET}`;
    const response = await getData(query, false, bodyData);

    if (response && response?.data) {
      setLoading(false);
      message.success("Cost Sheet Exported Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  // Effects
  useEffect(() => {
    getAllCostSheet();
  }, [refresh, getAllCostSheet]);

  useEffect(() => {
    getSalesContract();
  }, [getSalesContract]);

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const deleteSelectedCostSheet = async (name) => {
    setLoading(true);
    const updatedName = cutPath(name);
    const query = `${DELETE_COST_SHEET}`;
    const bodyData = {
      name: updatedName,
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setLoading(false);
      message.success("Cost Sheet Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openFile = (name) => {
    const URL = `${process.env.REACT_APP_BASE_URL}/storage/cost_sheet/${name}`;
    window.open(URL, "_blank", "noreferrer");
  };

  const tableColumns = [
    {
      title: "File Name",
      key: "bin_name",
      width: "90%",
      render: (_, record) => {
        console.log("record", record);

        return <div>{cutPath(record)}</div>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Space>
            <Button
              size="small"
              onClick={() => openFile(cutPath(record))}
              disabled={!hasPermission([COST_SHEET_REPORT_VIEW_PERMISSION])}
            >
              {hasPermission([COST_SHEET_REPORT_VIEW_PERMISSION])
                ? "Download"
                : "No Permission"}
            </Button>
            <Popconfirm
              title="Are you sure you want to delete?"
              onConfirm={() => {
                deleteSelectedCostSheet(record);
              }}
            >
              <Button
                type="primary"
                danger
                size="small"
                disabled={!hasPermission([COST_SHEET_REPORT_DELETE_PERMISSION])}
              >
                {hasPermission([COST_SHEET_REPORT_DELETE_PERMISSION])
                  ? "Delete"
                  : "No Permission"}
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={exportCostSheet}
                addButtonText={
                  hasPermission([COST_SHEET_REPORT_GENERATE_PERMISSION])
                    ? "Export"
                    : "No Permission"
                }
                disabled={
                  !hasPermission([COST_SHEET_REPORT_GENERATE_PERMISSION])
                }
                hideFilterButton
              />
            }
            title={<Title level={5}>Cost Sheet</Title>}
          />
        }
        content={
          <div style={{ paddingTop: 10 }}>
            <Form layout="vertical" form={costSheetform}>
              <Row gutter={6}>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="Sales Contract"
                    name="sales_contract_id"
                    rules={[
                      {
                        required: true,
                        message: "Please Select Sales Contract!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a Sales Contract"
                      onChange={(value) => {
                        getStyles(value);
                      }}
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      size="small"
                      onSearch={getSalesContract}
                      notFoundContent={
                        salesContractList && !salesContractList.length
                          ? "No sales contracts found to pre-select."
                          : undefined
                      }
                    >
                      {salesContractList?.length &&
                        salesContractList.map((item, index) => (
                          <Option value={item.id} key={index}>
                            {item.reference_no}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="Style"
                    name="style_id"
                    rules={[
                      {
                        required: true,
                        message: "Please Select Style!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a Style"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      disabled={stylesList?.length > 0 ? false : true}
                      size="small"
                      notFoundContent={
                        stylesList && !stylesList.length
                          ? "No styles found for this Sales Contract."
                          : undefined
                      }
                    >
                      {stylesList?.length &&
                        stylesList.map((item, index) => (
                          <Option value={item?.style_info?.id} key={index}>
                            {item?.style_info?.style_no}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="CM Cost"
                    name="cm_cost"
                    rules={[
                      {
                        required: true,
                        message: "Please Select CM Cost!",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="CM Cost"
                      precision={2}
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="Commercial Cost (%)"
                    name="commercial_cost"
                    rules={[
                      {
                        required: true,
                        message: "Please Select Commercial Cost!",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Commercial Cost"
                      precision={2}
                      size="small"
                    />
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item
                    label="Buying Comm (%)"
                    name="buying_comm"
                    rules={[
                      {
                        required: true,
                        message: "Please Select Buying Comm!",
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      placeholder="Buying Comm"
                      precision={2}
                      size="small"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <div className="table" style={{ marginTop: 10 }}>
              {/* <FilterComponent
                tableData={tableData}
                filterAPI={getAllCostSheet}
                filterName={"fabric_reconcil"}
                searchButtonText={"Export"}
                defaultOpen
                setShowFilter={setShowFilter}
                showFilter={showFilter}
              /> */}
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => refetch()}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
              />
            </div>
          </div>
        }
      />
    </>
  );
};

export default CostSheet;
